import IconAdd from '@apollo/icons/default/IconAdd.svg';
import IconClose from '@apollo/icons/default/IconClose.svg';
import IconRemove from '@apollo/icons/default/IconRemove.svg';
import IconSearch from '@apollo/icons/default/IconSearch.svg';
import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@apollo/orbit';
import { List } from '@apollo/space-kit/List';
import { ListItem } from '@apollo/space-kit/ListItem';
import classnames from 'classnames';
import { orderBy, partition, range } from 'lodash';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { GroupedValues } from 'src/app/graph/operationsPage/insightsL3Nav/insightsList/FieldsList';
import { useAriaDescribedBySetting } from 'src/hooks/useAriaDescribedBySetting';

import { ClickableText } from '../common/clickableText/ClickableText';
import { PlainHtmlButton } from '../common/plainHtmlButton/PlainHtmlButton';

// Copied from src/components/clientFilter/checkbox/Checkbox.tsx with some style
// updates
const Checkbox = ({
  checked,
  labelledBy,
  disabled,
}: {
  checked: boolean;
  labelledBy: string;
  disabled: boolean;
}) => {
  return checked ? (
    <IconRemove
      aria-checked={true}
      aria-labelledby={labelledBy}
      className={classnames(disabled ? 'text-icon-disabled' : 'text-link')}
    />
  ) : (
    <IconAdd
      aria-checked={false}
      aria-labelledby={labelledBy}
      className={classnames(
        disabled ? 'text-icon-disabled' : 'text-icon-primary',
      )}
    />
  );
};

const GroupDivider = ({
  currentValue,
  groupedValues,
}: {
  currentValue: string;
  groupedValues: GroupedValues;
}) => {
  return groupedValues.find((group) => group.dividerAfter === currentValue) ? (
    <Divider label="and" className="py-2" />
  ) : null;
};

const AddValueItem = ({
  onValueAdded,
}: {
  onValueAdded?: (value: string) => void;
}) => {
  const [valueToAdd, setValueToAdd] = React.useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Using event.stopPropagation() in React's onKeyDown doesn't work here,
    // since SpaceKit's modal is attached to a native DOM event.
    // Useful context:
    // https://gideonpyzer.dev/blog/2018/12/29/event-propagation-react-synthetic-events-vs-native-events/
    const inputRefCurrent = inputRef.current;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.code === 'Escape') {
        setValueToAdd('');
        event.stopPropagation();
      }
    }
    if (inputRefCurrent) {
      inputRefCurrent.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      if (inputRefCurrent) {
        inputRefCurrent.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [setValueToAdd]);

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent) => {
      if (
        event.key === 'Enter' &&
        valueToAdd &&
        valueToAdd.length > 0 &&
        onValueAdded
      ) {
        event.stopPropagation();
        onValueAdded(valueToAdd);
        setValueToAdd('');
      }
    },
    [onValueAdded, setValueToAdd, valueToAdd],
  );

  const handleClear = () => {
    setValueToAdd('');

    inputRef.current?.focus();
  };

  return (
    <List>
      <ListItem className="-mx-1 my-0.5 h-12 bg-white px-1 hover:bg-white">
        <div className="flex items-center space-x-2">
          <FormControl className="flex-1">
            <FormLabel className="sr-only">Add new value</FormLabel>
            <Input
              ref={inputRef}
              autoFocus
              placeholder="Add new value..."
              value={valueToAdd}
              onChange={(e) => setValueToAdd(e.target.value)}
              onKeyUp={handleKeyUp}
              rightElement={
                <IconClose
                  className={classnames(
                    'cursor-pointer rounded-full bg-silver-darker p-1 text-inverted',
                    !valueToAdd && 'hidden',
                  )}
                  role="button"
                  aria-label="Clear value to add"
                  tabIndex={0}
                  onClick={handleClear}
                  onKeyDown={(e) =>
                    e.key === 'Enter' ? handleClear() : undefined
                  }
                />
              }
            />
          </FormControl>
          <Button
            variant="primary"
            isDisabled={!valueToAdd}
            onClick={() => {
              onValueAdded?.(valueToAdd);
              setValueToAdd('');
            }}
            className="w-12 min-w-0"
          >
            Add
          </Button>
        </div>
      </ListItem>
    </List>
  );
};

const SelectItem = ({
  label,
  labelEnd,
  checked,
  onClick,
  onOnlyClick,
  disabled,
  canSelectExclusive,
}: {
  label: string;
  labelEnd?: string;
  checked: boolean;
  onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onOnlyClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  disabled: boolean;
  canSelectExclusive?: boolean;
}) => {
  const checkboxLabelledBy = useAriaDescribedBySetting('checkbox-label-');

  return (
    <ListItem
      className={classnames(
        'group my-0.5 mr-8 w-full px-2',
        checked
          ? disabled
            ? 'bg-silver-light font-semibold hover:bg-silver-light'
            : 'bg-blue-lightest font-semibold hover:bg-blue-lightest'
          : disabled
            ? 'font-normal hover:bg-transparent'
            : 'font-normal hover:bg-silver-light',
      )}
      as={
        <Button
          className={classnames(disabled ? 'cursor-default' : 'cursor-pointer')}
          variant="hidden"
          onClick={onClick}
        />
      }
    >
      <div className="flex items-center">
        <Checkbox
          checked={checked}
          labelledBy={checkboxLabelledBy}
          disabled={disabled}
        />
        <label
          className={classnames(
            'ml-2 truncate',
            disabled ? 'cursor-default' : 'cursor-pointer',
          )}
          id={checkboxLabelledBy}
          title={label}
        >
          {label}
        </label>
        {canSelectExclusive && (
          <div className="invisible flex-1 justify-self-end text-right group-hover:visible">
            <ClickableText
              className="ml-2 h-8 pl-2 font-normal text-link"
              data-testid="select-only-button"
              onClick={onOnlyClick}
              as={<PlainHtmlButton />}
            >
              only
            </ClickableText>
          </div>
        )}
        {checked && labelEnd && (
          <div
            className={classnames(
              'text-sm',
              canSelectExclusive ? 'group-hover:hidden' : 'ml-auto',
            )}
          >
            {labelEnd}
          </div>
        )}
      </div>
    </ListItem>
  );
};

// eslint-disable-next-line prefer-arrow-callback
const ItemList = memo(function InnerItemList<Value extends string>({
  items,
  labelForValue,
  isChecked,
  handleSelect,
  handleSelectExclusive,
  className,
  disableSelection,
  topMessage,
  labelEnd,
  pageSize = 50,
  canSelectExclusive,
  groupedValues,
}: {
  items: Array<Value>;
  labelForValue: ((value: Value) => string) | undefined;
  isChecked: (value: Value) => boolean;
  handleSelect: (value: Value) => void;
  handleSelectExclusive: (value: Value) => void;
  className?: string;
  disableSelection?: boolean;
  labelEnd?: ((value: Value) => string | undefined) | string;
  topMessage?: string;
  pageSize?: number;
  canSelectExclusive?: boolean;
  groupedValues?: GroupedValues;
}) {
  const [timesExpanded, setTimesExpanded] = useState(1);

  return (
    <List className={className}>
      {topMessage && (
        <div>
          <div>{topMessage}</div>
          <hr className="my-2 border-primary" />
        </div>
      )}
      {range(timesExpanded).map((i) => (
        <React.Fragment key={i}>
          {items.slice(pageSize * i, pageSize * (i + 1)).map((value) => (
            <div key={value}>
              <SelectItem
                label={labelForValue ? labelForValue(value) : value}
                checked={isChecked(value)}
                onClick={(evt) => {
                  evt.preventDefault();
                  handleSelect(value);
                }}
                onOnlyClick={(evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  handleSelectExclusive(value);
                }}
                labelEnd={
                  typeof labelEnd === 'function'
                    ? labelEnd(value)
                    : typeof labelEnd === 'string'
                      ? labelEnd
                      : undefined
                }
                disabled={!!disableSelection}
                canSelectExclusive={canSelectExclusive}
              />
              {groupedValues ? (
                <GroupDivider
                  currentValue={value}
                  groupedValues={groupedValues}
                />
              ) : null}
            </div>
          ))}
        </React.Fragment>
      ))}
      {pageSize * timesExpanded < items.length && (
        <Button
          className="mt-4 w-full"
          onClick={() => setTimesExpanded((s) => s + 1)}
        >
          See more...
        </Button>
      )}
    </List>
  );
});

export function MultiSelect<TValue extends string = string>({
  valueTitle,
  allValues,
  canSearch = true,
  canAddNewValues = false,
  canSelectExclusive = false,
  onValueAdded,
  selectedValues,
  setSelectedValues,
  triggerNode,
  selectionMode = 'multiple',
  labelForValue,
  orderValuesBy,
  className,
  valueTitleClassName,
  clearAllClassName,
  disabled = false,
  disableSelection = false,
  labelEnd,
  topMessage,
  clearAllText = 'Clear all',
  groupedValues,
  closeOnSelect = false,
}: {
  valueTitle: string;
  allValues: TValue[];
  canSearch?: boolean;
  canAddNewValues?: boolean;
  canSelectExclusive?: boolean;
  onValueAdded?: (value: string) => void;
  selectedValues: Set<TValue>;
  setSelectedValues: (newValues: Set<TValue>) => void;
  triggerNode: React.ReactElement;
  selectionMode?: 'single' | 'multiple';
  labelForValue?: (value: TValue) => string;
  orderValuesBy?: (value: TValue) => string | number;
  className?: string;
  valueTitleClassName?: string;
  clearAllClassName?: string;
  disabled?: boolean;
  disableSelection?: boolean;
  labelEnd?: ((value: TValue) => string | undefined) | string;
  topMessage?: string;
  clearAllText?: string;
  groupedValues?: GroupedValues;
  closeOnSelect?: boolean;
}) {
  const selectedValuesArray = useMemo(
    () => Array.from(selectedValues),
    [selectedValues],
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [filterTerm, setFilterTerm] = React.useState('');
  const shouldShowSearch = canSearch && allValues.length > 6;
  const [selectedFilteredValues, unSelectedFilteredValues] = useMemo(
    () =>
      partition(
        allValues.filter(
          (value) =>
            !shouldShowSearch ||
            value.toLowerCase().includes(filterTerm.toLowerCase()),
        ),
        (value) => selectedValues.has(value),
      ),
    [allValues, filterTerm, selectedValues, shouldShowSearch],
  );

  const isChecked = useCallback(
    (value: TValue) => selectedValues.has(value),
    [selectedValues],
  );

  const deleteOpposingValueFromSet = useCallback(
    (selectedValue: TValue, newValues: Set<TValue>, groups: GroupedValues) => {
      for (const group of groups) {
        const opposingValues: string[] = group.values;
        // If we have grouped values that are opposing, find the selectedValue in the group
        // and remove the others from the newValues Set. This makes it so the user can
        // select one or the other, like a radio button. The only difference is that you can deselect all values.
        if (group.opposing && opposingValues.includes(selectedValue)) {
          opposingValues.forEach((value) => {
            if (value !== selectedValue) newValues.delete(value as TValue);
          });
        }
      }
    },
    [],
  );

  const handleSelect = useCallback(
    (value: TValue, onClose: () => void) => {
      const checked = isChecked(value);
      if (checked) {
        const newValues = new Set(selectedValues);
        newValues.delete(value);
        setSelectedValues(newValues);
      } else {
        const newValues = new Set(
          selectionMode === 'multiple' ? selectedValues : [],
        );
        // check for groupedValues that are opposing
        if (groupedValues)
          deleteOpposingValueFromSet(value, newValues, groupedValues);
        newValues.add(value);
        setSelectedValues(newValues);
      }
      if (closeOnSelect) onClose();
    },
    [
      isChecked,
      selectedValues,
      selectionMode,
      setSelectedValues,
      deleteOpposingValueFromSet,
      groupedValues,
      closeOnSelect,
    ],
  );

  const handleSelectExclusive = useCallback(
    (value: TValue, onClose: () => void) => {
      setSelectedValues(new Set([value]));
      if (closeOnSelect) onClose();
    },
    [setSelectedValues, closeOnSelect],
  );

  const allValuesArray = useMemo(() => {
    if (shouldShowSearch) return [];
    const values =
      selectionMode === 'single'
        ? allValues
        : [...Array.from(selectedValues), ...unSelectedFilteredValues];
    return orderValuesBy ? orderBy(values, orderValuesBy) : values;
  }, [
    shouldShowSearch,
    selectionMode,
    allValues,
    selectedValues,
    unSelectedFilteredValues,
    orderValuesBy,
  ]);

  const handleClear = () => {
    setFilterTerm('');
    inputRef.current?.focus();
  };

  return (
    <Popover
      placement="bottom-start"
      isOpen={disabled ? false : undefined}
      preventOverflow
      boundary={document.body}
    >
      {({ onClose }) => (
        <>
          <PopoverTrigger>{triggerNode}</PopoverTrigger>
          <PopoverContent>
            <div className={classnames('p-4', className)}>
              <div className="flex items-center justify-between">
                <h3
                  className={classnames(
                    'mb-2 text-base font-semibold uppercase tracking-wider',
                    // eslint-disable-next-line local/no-conditional-class-names
                    valueTitleClassName,
                  )}
                >
                  {valueTitle}
                </h3>
                {selectedValues.size > 0 && (
                  <ClickableText
                    className={classnames(
                      'mb-2 rounded text-sm text-link focus-visible:ring-2',
                      // eslint-disable-next-line local/no-conditional-class-names
                      clearAllClassName,
                    )}
                    onClick={() => {
                      if (closeOnSelect) onClose();
                      setSelectedValues(new Set());
                    }}
                  >
                    {clearAllText}
                  </ClickableText>
                )}
              </div>
              {shouldShowSearch && (
                <>
                  <div>
                    <FormControl className="mb-4">
                      <FormLabel className="sr-only">Search</FormLabel>
                      <Input
                        className="min-w-72"
                        autoFocus
                        value={filterTerm}
                        placeholder="Search..."
                        onChange={(e) => setFilterTerm(e.target.value)}
                        ref={inputRef}
                        leftElement={
                          <IconSearch className="text-icon-primary" />
                        }
                        rightElement={
                          filterTerm && (
                            <IconClose
                              tabIndex={0}
                              role="button"
                              aria-label="Clear search"
                              className="cursor-pointer rounded-full bg-silver-darker p-1 text-inverted"
                              onClick={handleClear}
                              onKeyDown={(e) =>
                                e.key === 'Enter' ? handleClear() : undefined
                              }
                            />
                          )
                        }
                      />
                    </FormControl>
                  </div>
                  {canAddNewValues && (
                    <AddValueItem onValueAdded={onValueAdded} />
                  )}
                  {selectedValues.size > 0 && (
                    <>
                      <div className="text-base font-semibold ">Selected</div>
                      <ItemList
                        className="mb-4"
                        items={selectedValuesArray}
                        labelForValue={labelForValue}
                        isChecked={isChecked}
                        handleSelect={(value) => handleSelect(value, onClose)}
                        handleSelectExclusive={(value) =>
                          handleSelectExclusive(value, onClose)
                        }
                        canSelectExclusive={canSelectExclusive}
                        labelEnd={labelEnd}
                        disableSelection={disableSelection}
                        topMessage={topMessage}
                      />
                    </>
                  )}
                  {unSelectedFilteredValues.length === 0 && (
                    <>
                      <hr className="mb-4 border-primary" />
                      <div className="rounded bg-silver-light p-4 text-sm">
                        <div className="font-semibold">
                          {selectedFilteredValues.length > 0
                            ? 'All matches selected.'
                            : 'No matches found.'}
                        </div>
                        <div className="text-secondary">
                          Try changing your search criteria in the text input
                          above.
                        </div>
                      </div>
                    </>
                  )}
                  {unSelectedFilteredValues.length > 0 && (
                    <>
                      <div className="text-base font-semibold ">
                        {unSelectedFilteredValues.length} Results
                      </div>
                      <ItemList
                        items={unSelectedFilteredValues}
                        labelForValue={labelForValue}
                        isChecked={isChecked}
                        handleSelect={(value) => handleSelect(value, onClose)}
                        handleSelectExclusive={(value) =>
                          handleSelectExclusive(value, onClose)
                        }
                        canSelectExclusive={canSelectExclusive}
                        labelEnd={labelEnd}
                        disableSelection={disableSelection}
                        topMessage={topMessage}
                      />
                    </>
                  )}
                </>
              )}
              {!shouldShowSearch && (
                <>
                  {canAddNewValues && (
                    <AddValueItem onValueAdded={onValueAdded} />
                  )}

                  <ItemList
                    items={allValuesArray}
                    labelForValue={labelForValue}
                    isChecked={isChecked}
                    handleSelect={(value) => handleSelect(value, onClose)}
                    handleSelectExclusive={(value) =>
                      handleSelectExclusive(value, onClose)
                    }
                    canSelectExclusive={canSelectExclusive}
                    labelEnd={labelEnd}
                    disableSelection={disableSelection}
                    topMessage={topMessage}
                    groupedValues={groupedValues}
                  />
                </>
              )}
            </div>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}
